import { useState } from "react";

import { api } from "../api/client";
import type { WeatherDailyResponse } from "../types/api";
import { Button } from "./ds/Button";
import { Input } from "./ds/Input";

type Props = {
  lat: number | null;
  lon: number | null;
};

const ALL_LAYERS = ["tp", "t2m", "pev", "ssrd"] as const;
const MAX_DAYS = 366;

export function WeatherDailyPanel({ lat, lon }: Props) {
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set(ALL_LAYERS));
  const [data, setData] = useState<WeatherDailyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const canFetch =
    lat !== null && lon !== null && !!dateStart && !!dateEnd && selected.size > 0;

  function toggleLayer(code: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  async function fetchDaily() {
    if (!canFetch || lat === null || lon === null) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await api.weather.daily(lat, lon, {
        dateStart,
        dateEnd,
        layers: Array.from(selected),
        source: "era5",
      });
      setData(resp);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="rounded-xs border-[1.2px] border-mef-separator bg-white p-4 shadow-mef-card space-y-3">
      <div>
        <h3 className="mef-caption">Daily series (ERA5-Land)</h3>
        <p className="mt-1 text-[12px] leading-4 text-black/40">
          Window capped at {MAX_DAYS} days. ERA5-Land has a ~5-day publication lag.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="w-36">
          <Input
            id="daily-from"
            type="date"
            label="From"
            value={dateStart}
            onChange={(e) => setDateStart(e.target.value)}
          />
        </div>
        <div className="w-36">
          <Input
            id="daily-to"
            type="date"
            label="To"
            value={dateEnd}
            onChange={(e) => setDateEnd(e.target.value)}
          />
        </div>
        <div>
          <div className="mef-small-caption mb-0.5">Variables</div>
          <div className="flex gap-3 flex-wrap">
            {ALL_LAYERS.map((code) => (
              <label
                key={code}
                className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.04em] text-black/60 cursor-pointer mef-focus-ring"
              >
                <input
                  type="checkbox"
                  checked={selected.has(code)}
                  onChange={() => toggleLayer(code)}
                  className="h-3.5 w-3.5 accent-mef-green"
                />
                {code}
              </label>
            ))}
          </div>
        </div>
        <Button
          variant="primary"
          size="sm"
          disabled={!canFetch || loading}
          onClick={() => void fetchDaily()}
        >
          {loading ? "Fetching…" : "Fetch"}
        </Button>
      </div>

      {error && (
        <p className="text-[12px] leading-4 text-mef-red">{error}</p>
      )}

      {data && data.warnings && data.warnings.length > 0 && (
        <ul className="text-[11px] leading-4 text-amber-700 list-disc pl-5 space-y-0.5">
          {data.warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      )}

      {data && <DailyTable data={data} />}
    </div>
  );
}

function DailyTable({ data }: { data: WeatherDailyResponse }) {
  if (data.days.length === 0) {
    return (
      <p className="text-[13px] leading-4 text-black/40">
        No data for the requested window.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-left mef-small-caption">
            <th className="py-1 pr-3 font-medium">Date</th>
            <th className="py-1 px-2 text-right font-medium">Rain (m)</th>
            <th className="py-1 px-2 text-right font-medium">Temp (°C)</th>
            <th className="py-1 px-2 text-right font-medium">ET0 (m)</th>
            <th className="py-1 pl-2 text-right font-medium">Rg (MJ/m²/d)</th>
          </tr>
        </thead>
        <tbody>
          {data.days.map((d) => (
            <tr key={d.date} className="border-t border-mef-surface-2">
              <td className="py-1.5 pr-3 font-mono text-black/80">{d.date}</td>
              <td className="py-1.5 px-2 text-right tabular-nums text-black/80">
                {fmt(d.rain_m, 4)}
              </td>
              <td className="py-1.5 px-2 text-right tabular-nums text-black/80">
                {fmt(d.temp_c, 2)}
              </td>
              <td className="py-1.5 px-2 text-right tabular-nums text-black/80">
                {fmt(d.et0_m, 4)}
              </td>
              <td className="py-1.5 pl-2 text-right tabular-nums text-black/80">
                {fmt(d.rg_mj_m2, 2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function fmt(v: number | undefined | null, digits: number): string {
  return v === undefined || v === null ? "—" : v.toFixed(digits);
}
